import { useState, useEffect } from "react";
import { changePinOrPassword } from "../../services/panel/panelServices";
import { useAuth } from "../../context/AuthContext";
import toast from "../../utils/toast";

const EMPTY_FORM = { oldPassword: "", newPassword: "", confirmPassword: "" };

function PasswordInput({
  name,
  value,
  onChange,
  placeholder,
  error,
  disabled,
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={
          name === "oldPassword" ? "current-password" : "new-password"
        }
        style={{
          paddingRight: 42,
          ...(error ? { borderColor: "#dc2626" } : {}),
        }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--text-dim)",
          display: "flex",
        }}
      >
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
          <path
            d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <circle
            cx={12}
            cy={12}
            r={3}
            stroke="currentColor"
            strokeWidth="1.8"
          />
          {show && (
            <path
              d="M4 4l16 16"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          )}
        </svg>
      </button>
    </div>
  );
}

export default function ChangePasswordModal({ open, onClose }) {
  const { logout } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.oldPassword) e.oldPassword = "Enter your current password.";
    if (!form.newPassword) e.newPassword = "Enter a new password.";
    else if (form.newPassword === form.oldPassword)
      e.newPassword = "New password must be different from the current one.";
    if (form.confirmPassword !== form.newPassword)
      e.confirmPassword = "Passwords do not match.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        OldP: form.oldPassword,
        NewP: form.newPassword,
        IsPin: false,
      };
      const res = await changePinOrPassword(payload);
      if (res?.statuscode === 1) {
        toast.success(`${res?.msg} Please log in again.`);

        setTimeout(() => {
          logout();
          window.location.replace("/login");
        }, 1500);
      } else {
        toast.error(res?.msg);
        setSaving(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to change password.");
      setSaving(false);
    }
  };

  const errorText = (key) =>
    errors[key] ? (
      <span style={{ color: "#dc2626", fontSize: 11 }}>{errors[key]}</span>
    ) : null;

  return (
    <div
      className={`modal-overlay ${open ? "open" : ""}`}
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="modal-box" style={{ width: 420 }}>
        <h3>Change password</h3>
        <p>You will be logged out and asked to log in with the new password.</p>
        <div className="field">
          <label>Current password</label>
          <PasswordInput
            name="oldPassword"
            value={form.oldPassword}
            onChange={handleChange}
            placeholder="Enter current password"
            error={errors.oldPassword}
            disabled={saving}
          />
          {errorText("oldPassword")}
        </div>
        <div className="field">
          <label>New password</label>
          <PasswordInput
            name="newPassword"
            value={form.newPassword}
            onChange={handleChange}
            placeholder="Enter new password"
            error={errors.newPassword}
            disabled={saving}
          />
          {errorText("newPassword")}
        </div>
        <div className="field">
          <label>Confirm new password</label>
          <PasswordInput
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            placeholder="Re-enter new password"
            error={errors.confirmPassword}
            disabled={saving}
          />
          {errorText("confirmPassword")}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Changing..." : "Change password"}
          </button>
        </div>
      </div>
    </div>
  );
}
